import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProjectById, getServiceForProject, getTestimonialsForProject } from '../utils/dataModel';
import SEO from './SEO';

const ProjectPage = () => {
  const { projectId } = useParams();
  const project = getProjectById(projectId);
  const service = project ? getServiceForProject(projectId) : null;
  const testimonials = project ? getTestimonialsForProject(projectId) : [];

  // Reset scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  if (!project) {
    return (
      <div className="project-page not-found">
        <div className="container">
          <h1>Project Not Found</h1>
          <p>The project you're looking for doesn't exist.</p>
          <Link to="/" className="btn-primary">Return Home</Link>
        </div>
      </div>
    );
  }

  const seoTitle = `${project.title} - ${project.client_name} | SolidlyAI`;
  const seoDescription = project.problem_statement;

  return (
    <>
      <SEO 
        title={seoTitle}
        description={seoDescription}
        type="article"
      />
      
      <div className="project-page">
        <div className="project-header">
          <div className="container">
            <div className="project-breadcrumb">
              <Link to="/">Home</Link>
              {service && (
                <>
                  <span> / </span>
                  <Link to={`/services/${service.id}`}>{service.title}</Link>
                </>
              )}
              <span> / </span>
              <span>{project.client_name}</span>
            </div>
            
            <div className="project-header-content">
              <div className="project-logo-large">
                <img 
                  src={project.logo} 
                  alt={`${project.client_name} logo`}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div className="project-icon-large" style={{display: 'none'}}>{project.icon}</div>
              </div>
              <div className="project-header-text">
                <h1>{project.title}</h1>
                <p className="project-client-large">{project.client_name}</p>
                {service && (
                  <div className="project-service-tag">
                    <Link to={`/services/${service.id}`}>
                      {service.title}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="project-content">
          <div className="container">
            <section className="project-overview">
              <div className="overview-grid">
                <div className="problem-statement">
                  <h2>The Challenge</h2>
                  <p>{project.problem_statement}</p>
                </div>
                
                <div className="solution-overview">
                  <h2>Our Solution</h2>
                  <p>{project.solution}</p>
                </div>
              </div>
            </section>

            <section className="project-details">
              <h2>What We Delivered</h2>
              <ul className="details-list">
                {project.details.map((detail, index) => (
                  <li key={index} className="detail-item">
                    <span className="detail-icon">✓</span>
                    {detail}
                  </li>
                ))}
              </ul>
            </section>

            {testimonials.length > 0 && (
              <section className="project-testimonials">
                <h2>What Our Clients Say</h2>
                <div className="testimonials-list">
                  {testimonials.map((testimonial) => (
                    <div key={testimonial.id} className="testimonial-card">
                      <div className="testimonial-content">
                        <p className="testimonial-body">"{testimonial.body}"</p>
                      </div>
                      <div className="testimonial-author">
                        <img 
                          src={testimonial.picture} 
                          alt={testimonial.name}
                          className="testimonial-picture"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <div className="testimonial-info">
                          <p className="testimonial-name">{testimonial.name}</p>
                          <p className="testimonial-title">{testimonial.title}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="project-cta">
              <div className="cta-content">
                <h2>Interested in Similar Results?</h2>
                <p>Let's discuss how we can help solve your challenges with proven expertise.</p>
                <div className="cta-buttons">
                  <Link to="/#contact" className="btn btn-primary">Start a Conversation</Link>
                  {service && (
                    <Link to={`/services/${service.id}`} className="btn btn-secondary">
                      Explore {service.title}
                    </Link>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectPage;